import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { trackPendingSave } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Car, Camera, Ruler, ClipboardList, FlaskConical, ShieldCheck, FileText, Save } from "lucide-react";
import InspectionPanel from "@/components/workshop/InspectionPanel";
import { ProductsUsedPanel, TechSheetPanel, ProtectionPanel } from "@/components/workshop/TechSheetPanels";
import { PhotosPanel, PtgPanel, ReportPanel } from "@/components/workshop/WorkshopPanels";
import { Btn } from "@/components/workshop/ui";
import { usePageGuard } from "@/lib/unsavedGuard";

export const WORKSHOP_TABS = [
  { key: "etat", label: "État des lieux", icon: Car },
  { key: "photos", label: "Photos", icon: Camera },
  { key: "ptg", label: "Mesures peinture", icon: Ruler },
  { key: "fiche", label: "Fiche technique", icon: ClipboardList },
  { key: "produits", label: "Produits utilisés", icon: FlaskConical },
  { key: "protection", label: "Protection", icon: ShieldCheck },
  { key: "rapport", label: "Rapport", icon: FileText },
];
const SHEET_TABS = ["fiche", "produits", "protection"];
const eurF = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const dur = (s) => { const m = Math.round((s || 0) / 60); return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, "0")}` : `${m} min`; };
const INV_ST = { brouillon: "Brouillon", envoyee: "Envoyée", payee: "Payée", partielle: "Partiellement payée", en_retard: "En retard", annulee: "Annulée" };

function WorkshopSummary({ ws, navigate }) {
  const a = ws.appointment; const b = ws.billing; const t = ws.timing || {};
  const planned = t.planned_minutes || (a.start && a.end ? Math.round((new Date(a.end) - new Date(a.start)) / 60000) : null);
  const Item = ({ label, children, testid }) => <div className="min-w-0" data-testid={testid}><div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div><div className="text-sm font-semibold text-slate-800 break-words">{children}</div></div>;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="workshop-summary">
      <Item label="Prix prévu" testid="ws-sum-price">{eurF(ws.billed)}</Item>
      <Item label="Durée estimée">{planned ? dur(planned * 60) : "—"}</Item>
      <Item label="Temps réel" testid="ws-sum-time">{t.real_seconds ? dur(t.real_seconds) : "—"}{t.timer_state === "running" ? " (en cours)" : ""}</Item>
      {b && <Item label="Devis" testid="ws-sum-quote">{b.quotes?.length ? b.quotes.map((q) => <button key={q.id} onClick={() => navigate("/quotes")} className="text-emerald-700 underline mr-2">{q.number || "Devis"}</button>) : "—"}</Item>}
      {b && <Item label="Facture" testid="ws-sum-invoice">{b.invoice ? <button onClick={() => navigate("/invoices")} className="text-emerald-700 underline">{b.invoice.number || "Facture"} · {INV_ST[b.invoice.status] || b.invoice.status}</button> : "Non facturé"}</Item>}
      {b && <Item label="Paiements" testid="ws-sum-payments">{b.payments?.length ? eurF(b.payments.reduce((s, p) => s + (Number(p.amount) || 0), 0)) : "—"}</Item>}
      <WorkshopLinks ws={ws} navigate={navigate} Item={Item} />
    </div>
  );
}

const FU_ST = { envoyee: "envoyée", planifiee: "planifiée", a_envoyer: "à envoyer", sans_email: "sans e-mail", expiree: "expirée" };
function WorkshopLinks({ ws, navigate, Item }) {
  const l = ws.links; if (!l) return null;
  const qo = l.quote_online; const m = l.inspection_email; const fu = l.followups || [];
  return (
    <>
      <Item label="Fiches liées" testid="ws-sum-links">
        {l.client_id && <button onClick={() => navigate(`/clients/${l.client_id}`)} className="text-emerald-700 underline mr-2">Client</button>}
        {l.vehicle_id && <button onClick={() => navigate(`/vehicles/${l.vehicle_id}`)} className="text-emerald-700 underline mr-2">Véhicule</button>}
        {l.portal_url && <a href={l.portal_url} target="_blank" rel="noreferrer" className="text-emerald-700 underline">Portail client</a>}
      </Item>
      {qo && <Item label="Devis en ligne" testid="ws-sum-quote-online">{qo.accepted_at ? `Signé par ${qo.accepted_name || "le client"}` : qo.status || "—"}{qo.deposit_paid ? ` · acompte ${eurF(qo.deposit_paid_amount)} payé${qo.deposit_remaining != null ? `, reste ${eurF(qo.deposit_remaining)}` : ""}` : qo.deposit_status === "refuse" ? " · acompte refusé" : ""}</Item>}
      <Item label="Rapport envoyé" testid="ws-sum-report-mail">{m ? `${m.status === "echec" ? "Échec" : m.opened ? "Ouvert" : m.delivered ? "Délivré" : "Envoyé"} · ${new Date(m.created_at).toLocaleDateString("fr-FR")}` : "—"}{l.restitution_sent ? " · restitution envoyée" : ""}</Item>
      <Item label="Stock" testid="ws-sum-stock">{l.stock_deducted ? `Déduit${l.stock_auto ? " (auto)" : ""}` : "Non déduit"}</Item>
      <Item label="Montages" testid="ws-sum-montages">{l.montages || 0}</Item>
      <Item label="Relances prévues" testid="ws-sum-followups">{fu.length ? fu.map((f) => `${f.label} ${new Date(f.due_date).toLocaleDateString("fr-FR")} (${FU_ST[f.status] || f.status})`).join(" · ") : "—"}</Item>
    </>
  );
}

export default function Workshop() {
  const guardRef = usePageGuard();
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = WORKSHOP_TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "etat";
  const fromAppt = params.get("from") === "appt";
  // Ouvert depuis la fiche d'un RDV : le retour rouvre cette fiche (mécanisme existant /calendar?appt=ID).
  const [ws, setWs] = useState(null);
  const [sheet, setSheet] = useState({});
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  // Modifications non enregistrées : jamais perdues en quittant la fiche (retour, menu, geste retour du téléphone).
  const savedRef = useRef(null);
  const sheetRef = useRef(sheet);
  sheetRef.current = sheet;
  const isDirty = () => savedRef.current !== null && JSON.stringify(sheetRef.current) !== savedRef.current;

  const load = useCallback(() => api.get(`/appointments/${id}/workshop`).then((r) => { setWs(r.data); setSheet(r.data.tech_sheet || {}); savedRef.current = JSON.stringify(r.data.tech_sheet || {}); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), [id]);
  useEffect(() => { load(); api.get("/tools/products").then((r) => setProducts(r.data)).catch(() => {}); }, [load]);

  const saveSheet = async (next = sheet) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/appointments/${id}/tech-sheet`, next);
      setWs(data); setSheet(data.tech_sheet || {}); sheetRef.current = data.tech_sheet || {}; savedRef.current = JSON.stringify(data.tech_sheet || {});
      if (data.warning) toast.warning(data.warning); else toast.success("Fiche technique enregistrée");
      return true;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; } finally { setSaving(false); }
  };
  const goBack = async () => {
    if (isDirty() && !(await saveSheet(sheetRef.current))) return;
    if (fromAppt) navigate(`/calendar?appt=${id}`, { replace: true }); else if (window.history.length > 1) navigate(-1); else navigate("/calendar");
  };
  useEffect(() => {
    const warn = (e) => { if (isDirty()) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
      if (isDirty()) trackPendingSave(api.put(`/appointments/${id}/tech-sheet`, sheetRef.current));
    };
  }, [id]);

  if (!ws) return <div className="flex items-center justify-center h-64"><Loader2 className="w-7 h-7 animate-spin text-emerald-600" /></div>;
  const a = ws.appointment;
  const date = a.start ? new Date(a.start).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "";

  return (
    <div ref={guardRef} className="max-w-5xl mx-auto pb-40" data-testid="workshop-page">
      <div className="flex items-start gap-3 mb-4">
        <button data-testid="workshop-back" onClick={goBack} className="mt-1 text-slate-500"><ArrowLeft className="w-5 h-5" /></button>
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold text-slate-900">Dossier atelier</h1>
          <p className="text-sm text-slate-500 truncate" data-testid="workshop-subtitle">{[ws.client_name, ws.vehicle_name, date].filter(Boolean).join(" · ")}</p>
          {!!ws.services?.length && <p className="text-xs text-slate-400 truncate">{ws.services.join(", ")}</p>}
        </div>
      </div>
      <WorkshopSummary ws={ws} navigate={navigate} />
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1 ced-scroll-thin" data-testid="workshop-tabs">
        {WORKSHOP_TABS.map((t) => (
          <button key={t.key} data-testid={`ws-tab-${t.key}`} onClick={() => setParams(fromAppt ? { tab: t.key, from: "appt" } : { tab: t.key }, { replace: true })}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium border transition-colors ${tab === t.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>
      {tab === "etat" && <InspectionPanel key={ws.inspection?.id || "new"} ws={ws} reload={load} />}
      {tab === "photos" && <PhotosPanel ws={ws} reload={load} sheet={sheet} saveSheet={saveSheet} />}
      {tab === "ptg" && <PtgPanel ws={ws} reload={load} />}
      {tab === "fiche" && <TechSheetPanel sheet={sheet} setSheet={setSheet} ws={ws} />}
      {tab === "produits" && <ProductsUsedPanel aid={id} sheet={sheet} setSheet={setSheet} products={products} canMargins={ws.tech_sheet?.can_view_margins} billed={ws.tech_sheet?.summary?.billed ?? 0} />}
      {tab === "protection" && <ProtectionPanel sheet={sheet} setSheet={setSheet} products={products} ws={ws} />}
      {tab === "rapport" && <ReportPanel ws={ws} />}
      {SHEET_TABS.includes(tab) && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 z-30 bg-white/95 backdrop-blur border-t border-slate-200 p-3 flex justify-end">
          <Btn data-testid="ws-save-sheet" onClick={() => saveSheet()} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer la fiche</Btn>
        </div>
      )}
    </div>
  );
}
